import { useMemo } from 'react';
import { Trade } from 'utils/shared/trades/types';
import aggregateFlowBuckets, {
  backfillLeadingPrices,
  getTradeDateCount,
  isSignedFlowMetric,
  toInterval,
} from 'utils/shared/trades/aggregateFlowBuckets';

export type {
  BucketSize,
  BucketUnit,
  BucketInterval,
  BucketSizeValue,
  FlowMetric,
  BucketData,
} from 'utils/shared/trades/aggregateFlowBuckets';

import type {
  BucketSize,
  BucketInterval,
  BucketSizeValue,
  FlowMetric,
  BucketData,
} from 'utils/shared/trades/aggregateFlowBuckets';

export { getTradeDateCount, isSignedFlowMetric };

export default function useBucketedFlowData(
  trades: Trade[] | undefined,
  bucketSize: BucketSizeValue,
  metric: FlowMetric,
  ticker?: string,
  displayTimezone?: string,
): BucketData[] {
  return useMemo(() => {
    if (!trades || trades.length === 0) return [];
    const { buckets } = aggregateFlowBuckets(trades, bucketSize, metric, ticker, displayTimezone);
    if (buckets.length === 0) return [];

    if (isSignedFlowMetric(metric)) {
      backfillLeadingPrices(buckets);
      return buckets;
    }

    // Chart stacks puts below zero for unsigned activity metrics. Greek
    // exposure values are already signed by trade side and stay untouched.
    const out: BucketData[] = buckets.map((b) => ({
      ...b,
      putValue: -Math.abs(b.putValue),
    }));

    backfillLeadingPrices(out);
    return out;
  }, [trades, bucketSize, metric, ticker, displayTimezone]);
}

export const METRIC_OPTIONS: { value: FlowMetric; label: string }[] = [
  { value: 'premium', label: 'Premium' },
  { value: 'volume', label: 'Volume' },
  { value: 'avgIV', label: 'Avg IV' },
  { value: 'deltaExposure', label: 'Delta Exposure' },
  { value: 'gammaExposure', label: 'Gamma Exposure' },
  { value: 'vegaExposure', label: 'Vega Exposure' },
  { value: 'thetaExposure', label: 'Theta Exposure' },
];

export const BUCKET_SIZE_OPTIONS: { value: BucketSize; label: string }[] = [
  { value: 1, label: '1m' },
  { value: 5, label: '5m' },
  { value: 15, label: '15m' },
  { value: 30, label: '30m' },
  { value: 60, label: '60m' },
];

export const MULTI_DAY_BUCKET_OPTIONS: { value: BucketInterval; label: string }[] = [
  { value: { unit: 'h', size: 1 }, label: '1h' },
  { value: { unit: 'h', size: 2 }, label: '2h' },
  { value: { unit: 'h', size: 3 }, label: '3h' },
  { value: { unit: 'h', size: 4 }, label: '4h' },
  { value: { unit: 'd', size: 1 }, label: '1d' },
];

/**
 * Compares two bucket size values for equality. Use this in the chart's
 * "active" check since BucketInterval objects won't pass referential equality.
 */
export function isSameBucketValue(a: BucketSizeValue, b: BucketSizeValue): boolean {
  const ai = toInterval(a);
  const bi = toInterval(b);
  return ai.unit === bi.unit && ai.size === bi.size;
}

/**
 * Picks the appropriate bucket option set based on how many ET dates the
 * trades span. ≤1 day → minute options; >1 day → hour/day options.
 */
export function getBucketOptionsForTrades(
  trades: Trade[] | undefined,
  ticker?: string,
): { value: BucketSizeValue; label: string }[] {
  return getTradeDateCount(trades, ticker) > 1
    ? MULTI_DAY_BUCKET_OPTIONS
    : BUCKET_SIZE_OPTIONS;
}

export const DEFAULT_SINGLE_DAY_BUCKET: BucketSize = 15;
export const DEFAULT_MULTI_DAY_BUCKET: BucketInterval = { unit: 'h', size: 1 };

export const METRIC_Y_LABELS: Record<FlowMetric, { label: string; prefix: string }> = {
  premium: { label: 'Premium', prefix: '$' },
  volume: { label: 'Contracts', prefix: '' },
  avgIV: { label: 'Implied Vol', prefix: '' },
  deltaExposure: { label: 'Delta Exposure', prefix: '$' },
  gammaExposure: { label: 'Gamma Exposure', prefix: '$' },
  vegaExposure: { label: 'Vega Exposure', prefix: '$' },
  thetaExposure: { label: 'Theta Exposure', prefix: '$' },
};
